import * as React from 'react';
import WbCloudyOutlinedIcon from '@mui/icons-material/WbCloudyOutlined';
import { UserContext } from '../../context/UserContext';
import AccessDenied from '../shared/AccessDenied';
import LoadingScreen from '../../components/shared/loading/LoadingScreen';
import PanelLeftMenuWrapper, { MenuItem } from '../../components/shared/nav/PanelLeftMenuWrapper';
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import DataSaverOffOutlinedIcon from '@mui/icons-material/DataSaverOffOutlined';
import AirIcon from '@mui/icons-material/Air';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import EmojiFlagsOutlinedIcon from '@mui/icons-material/EmojiFlagsOutlined';
import InputOutlinedIcon from '@mui/icons-material/InputOutlined';
import SignalCellularAltOutlinedIcon from '@mui/icons-material/SignalCellularAltOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import { Box, MenuItem as MuiMenuItem, TextField } from '@mui/material';
import { Company, Group } from '@esg/esg-global-types';
import { getGroups } from '../../graphql/services/group.service';
import ConfirmationModal from '../../components/shared/modal/ConfirmationModal';
import PanelStandards from '../../components/configuration/standards/PanelStandards';
import PanelMetricGroups from '../../components/configuration/metric_groups/PanelMetricGroups';
import PanelMetrics from '../../components/configuration/metrics/PanelMetrics';
import PanelEmissionFactors from '../../components/configuration/emission_factors/PanelEmissionFactors';
import PanelSubscriptions from '../../components/configuration/subscriptions/PanelSubscriptions';
import PanelRoles from '../../components/configuration/roles/PanelRoles';
import PanelSectors from '../../components/configuration/sectors/PanelSectors';
import PanelCountries from '../../components/configuration/countries/PanelCountries';
import PanelRegions from '../../components/configuration/regions/PanelRegions';
import PanelPBIReports from '../../components/configuration/pbi_reports/PanelPBIReports';
import { syncMasterListMetrics } from '../../graphql/services/metric.service';
import { generateUuidV4 } from '../../util/uuid';
import { MetadataError } from '@ep/error-handling';
import { log } from '../../util/log';
import { FeedbackSnackbarContext } from '../../context/FeedbackSnackbarContext';
import PrimaryButton from '../../components/shared/button/PrimaryButton';
import BuildIcon from '@mui/icons-material/Build';
import { Heading } from '../../components/shared/typography/PrimaryTypography';
import PanelTridiumLinks from '../../components/configuration/tridium_links/PanelTridiumLinks';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import PanelResourceCentre from '../../components/configuration/resource_centre/PanelResourceCentre';
import { useModuleDurationTracking } from '../../hooks/useModuleDurationTracking';
import PanelGCPLinks from '../../components/configuration/gcp_links/PanelGCPLinks';

interface MasterViewElement {
  view: () => React.ReactNode;
  syncFunction?: (groupId?: string, companyId?: string) => Promise<void>;
}

const menu_items: Array<MenuItem> = [
  {
    id: 'countries',
    icon: <EmojiFlagsOutlinedIcon />,
    label: 'Countries'
  },
  {
    id: 'regions',
    icon: <LocationOnOutlinedIcon />,
    label: 'Regions'
  },
  {
    id: 'gcp_links',
    icon: <WbCloudyOutlinedIcon />,
    label: 'GCP Links'
  },
  {
    id: 'pbi_reports',
    icon: <SignalCellularAltOutlinedIcon />,
    label: 'PBI Reports'
  },
  {
    id: 'tridium_links',
    icon: <BuildIcon />,
    label: 'Tridium'
  },
  {
    id: 'metric_capture',
    label: 'Capture',
    icon: <InputOutlinedIcon />,
    child_items: [
      {
        id: 'standards',
        icon: <ChecklistRtlIcon />,
        label: 'Standards'
      },
      {
        id: 'metric_groups',
        icon: <DataSaverOffOutlinedIcon />,
        label: 'Metric Groups'
      },
      {
        id: 'metrics',
        icon: <AssessmentOutlinedIcon />,
        label: 'Metrics'
      },
      {
        id: 'emission_factors',
        icon: <AirIcon />,
        label: 'Emission Factors'
      }
    ]
  },
  {
    id: 'resource_centre',
    icon: <SchoolOutlinedIcon />,
    label: 'Resource Centre'
  },
  {
    id: 'sectors',
    icon: <BusinessCenterOutlinedIcon />,
    label: 'Sectors'
  },
  {
    id: 'roles',
    icon: <AssignmentIndOutlinedIcon />,
    label: 'Roles'
  },
  {
    id: 'subscriptions',
    icon: <PaidOutlinedIcon />,
    label: 'Subscriptions'
  }
];

const master_views_elements_map: Map<string, MasterViewElement> = new Map([
  [
    'countries',
    {
      view: () => <PanelCountries master_list />
    }
  ],
  [
    'regions',
    {
      view: () => <PanelRegions master_list />
    }
  ],
  [
    'gcp_links',
    {
      view: () => <PanelGCPLinks />
    }
  ],
  [
    'pbi_reports',
    {
      view: () => <PanelPBIReports />
    }
  ],
  [
    'tridium_links',
    {
      view: () => <PanelTridiumLinks />
    }
  ],
  [
    'standards',
    {
      view: () => <PanelStandards master_list />
    }
  ],
  [
    'metric_groups',
    {
      view: () => <PanelMetricGroups master_list />
    }
  ],
  [
    'metrics',
    {
      view: () => <PanelMetrics master_list />,
      syncFunction: (groupId?: string, companyId?: string) =>
        syncMasterListMetrics({ group_id: groupId, company_id: companyId }).then(() => undefined)
    }
  ],
  [
    'emission_factors',
    {
      view: () => <PanelEmissionFactors master_list />
    }
  ],
  [
    'resource_centre',
    {
      view: () => <PanelResourceCentre />
    }
  ],
  [
    'sectors',
    {
      view: () => <PanelSectors master_list />
    }
  ],
  [
    'roles',
    {
      view: () => <PanelRoles />
    }
  ],
  [
    'subscriptions',
    {
      view: () => <PanelSubscriptions />
    }
  ]
]);

/**
 * View for displaying master list database data allowing for creating, updating and deleting
 * @returns {JSX.Element}
 */
const MasterViewDashboard = () => {
  // Track module duration.
  useModuleDurationTracking('Master Configuration', 'N/A');

  const { user } = React.useContext(UserContext);
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const [selectedView, setSelectedView] = React.useState<MenuItem>(menu_items[0]);
  const [loadingSync, setLoadingSync] = React.useState<boolean>(false);
  const [groups, setGroups] = React.useState<Array<Group>>([]);
  const [syncGroupId, setSyncGroupId] = React.useState<string>('');
  const [syncCompanyId, setSyncCompanyId] = React.useState<string>('');
  const [confirmSyncOpen, setConfirmSyncOpen] = React.useState<boolean>(false);

  const current_master_view_element: MasterViewElement | undefined = master_views_elements_map.get(
    selectedView.id
  );

  const syncCompanies: Array<Company> = React.useMemo(
    () => groups.find((group) => group.id === syncGroupId)?.companies ?? [],
    [groups, syncGroupId]
  );

  const syncCompanyName: string | undefined = syncCompanies.find(
    (company) => company.id === syncCompanyId
  )?.name;

  React.useEffect(() => {
    (async () => {
      try {
        const fetched_groups: Array<Group> = await getGroups();
        setGroups(fetched_groups);
      } catch (err: unknown) {
        const tracking_id: string = generateUuidV4();
        log(
          'error',
          new MetadataError(
            err instanceof Error
              ? err.message
              : 'Error: MasterViewDashboard failed on an unknown error while calling getGroups.',
            {},
            tracking_id
          )
        );
        setFeedbackData({
          message: `Unable to load Groups for Master List Sync. Tracking ID: ${tracking_id}`,
          state: true,
          type: 'error'
        });
      }
    })();
  }, []);

  const handleChangeSelectedView = (view: MenuItem): void => {
    setSelectedView(view);
  };

  const handleMasterListSync = async (): Promise<void> => {
    const [groupId, companyId]: [string | undefined, string | undefined] =
      syncGroupId && syncCompanyId ? [syncGroupId, syncCompanyId] : [undefined, undefined];
    try {
      setLoadingSync(true);
      current_master_view_element?.syncFunction &&
        (await current_master_view_element.syncFunction(groupId, companyId));
      setFeedbackData({
        message: companyId
          ? `Master List Sync Successful for ${syncCompanyName ?? companyId}`
          : `Master List Sync Successful`,
        state: true,
        type: 'success'
      });
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: MasterViewDashboard failed on an unknown error while calling handleMasterListSync.',
          {
            current_master_view_element: current_master_view_element,
            group_id: groupId,
            company_id: companyId
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to Sync from Master List. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setLoadingSync(false);
    }
  };

  const handleSyncClick = async (): Promise<void> => {
    // Deliberately asymmetric: a company-scoped sync only ever touches the one selected company,
    // so it runs immediately. The unscoped ("sync all companies") path is the actually dangerous
    // one — it rewrites every company across every group — so that's the one gated behind the
    // confirmation modal, not the other way around.
    if (syncGroupId && syncCompanyId) {
      await handleMasterListSync();
    } else {
      setConfirmSyncOpen(true);
    }
  };

  return user === null ? (
    <LoadingScreen />
  ) : user.super_admin ? (
    <PanelLeftMenuWrapper
      header="MASTER LISTS"
      items={menu_items}
      handleMenuSelect={handleChangeSelectedView}
    >
      <Box sx={{ mx: 4 }}>
        <Box sx={{ display: 'flex', gap: 2, my: 6 }}>
          <Heading weight="Medium">{selectedView.label}</Heading>
          {current_master_view_element?.syncFunction && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', ml: 'auto' }}>
              <TextField
                select
                size="small"
                label="Group"
                value={syncGroupId}
                onChange={(e) => {
                  setSyncGroupId(e.target.value);
                  setSyncCompanyId('');
                }}
                sx={{ minWidth: 180 }}
              >
                <MuiMenuItem value="">All groups</MuiMenuItem>
                {groups.map((group) => (
                  <MuiMenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MuiMenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Company"
                value={syncCompanyId}
                onChange={(e) => setSyncCompanyId(e.target.value)}
                disabled={!syncGroupId || syncCompanies.length === 0}
                helperText={
                  syncGroupId && syncCompanies.length === 0
                    ? 'This group has no companies.'
                    : undefined
                }
                sx={{ minWidth: 180 }}
              >
                <MuiMenuItem value="" disabled>
                  Select a company
                </MuiMenuItem>
                {syncCompanies.map((company) => (
                  <MuiMenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MuiMenuItem>
                ))}
              </TextField>
              <ConfirmationModal
                open={confirmSyncOpen}
                title="Sync all companies"
                description="This rewrites configured metrics for every company."
                confirm_button_text="Sync all companies"
                confirm_loading={loadingSync}
                handleCloseModal={() => setConfirmSyncOpen(false)}
                handleConfirm={async () => await handleMasterListSync()}
              />
              <PrimaryButton
                loading={loadingSync}
                startIcon={<SyncIcon />}
                palette_color="success"
                disabled={Boolean(syncGroupId) && !syncCompanyId}
                onClick={async () => await handleSyncClick()}
              >
                {syncGroupId && syncCompanyId
                  ? `Sync ${syncCompanyName ?? ''}`
                  : 'Sync all companies'}
              </PrimaryButton>
            </Box>
          )}
        </Box>
        {current_master_view_element && current_master_view_element.view()}
      </Box>
    </PanelLeftMenuWrapper>
  ) : (
    <AccessDenied />
  );
};

export default MasterViewDashboard;
